// apps/web/src/hooks/useApiClient.ts
// WHY: Shared fetch wrapper that adds the API base URL and Authorization header
// from the Amplify auth session. Eliminates duplicated fetch logic across hooks
// and pages.

import { fetchAuthSession } from 'aws-amplify/auth';

const API_URL = (import.meta.env['VITE_API_URL'] as string | undefined) ?? '';

/**
 * Structured API error surfaced by the fetch helpers when the server returns
 * a non-2xx response. Carries the HTTP status and the parsed JSON body so
 * callers can surface RFC 7807 field-level validation errors inline.
 *
 * WHY: The previous helpers threw a plain Error with only `message`, which
 * forced callers to string-match their way to field details. ApiError keeps
 * the legacy `message` format (`"API error 400: Bad Request"`) so existing
 * `err instanceof Error` and `err.message.includes(...)` callers keep working,
 * while adding `.status` and `.body` for newer consumers.
 */
export class ApiError extends Error {
  readonly status: number;
  readonly statusText: string;
  readonly body: unknown;

  constructor(status: number, statusText: string, body: unknown) {
    super(`API error ${status}: ${statusText}`);
    this.name = 'ApiError';
    this.status = status;
    this.statusText = statusText;
    this.body = body;
  }
}

/** RFC 7807 field-level validation error. */
export interface Rfc7807FieldError {
  field: string;
  message: string;
}

/** Shape of the Synapse API's RFC 7807 error body. */
export interface Rfc7807Body {
  type?: string;
  title?: string;
  status?: number;
  detail?: string;
  instance?: string;
  correlationId?: string;
  fieldErrors?: Rfc7807FieldError[];
}

/**
 * Narrow an unknown error to an ApiError so callers can access `.status` and
 * `.body` without manual type guards. Returns null for non-API errors.
 */
export function asApiError(err: unknown): ApiError | null {
  return err instanceof ApiError ? err : null;
}

/**
 * Extract RFC 7807 field errors from an unknown error if present, otherwise
 * return an empty array.
 */
export function getFieldErrors(err: unknown): Rfc7807FieldError[] {
  const api = asApiError(err);
  if (!api) return [];
  const body = api.body as Rfc7807Body | null | undefined;
  return body?.fieldErrors ?? [];
}

async function parseErrorBody(res: Response): Promise<unknown> {
  // WHY: Error bodies may be empty (204-style failures) or non-JSON (proxy
  // errors). Swallow parse failures so the caller still gets a structured
  // ApiError with status + statusText even when the body is unusable.
  try {
    const text = await res.text();
    if (text.length === 0) return null;
    return JSON.parse(text) as unknown;
  } catch {
    return null;
  }
}

async function getAuthHeaders(): Promise<Record<string, string>> {
  try {
    const session = await fetchAuthSession();
    // WHY: Send the ID token, not the access token. The backend verifies ID
    // tokens because custom:* attributes (like custom:entra_groups used for
    // SAML role mapping) appear only on ID tokens — never on access tokens.
    // SOURCE: AWS Cognito docs on token types.
    const token = session.tokens?.idToken?.toString();
    if (token) {
      return { Authorization: `Bearer ${token}` };
    }
  } catch {
    // WHY: Fall through to empty headers — the API will return 401 and
    // TanStack Query will surface the error state.
  }
  return {};
}

/**
 * Fetch JSON from the Synapse API with automatic auth headers.
 * Throws on non-2xx responses so TanStack Query treats them as errors.
 */
export async function apiFetch<T>(path: string): Promise<T> {
  const headers = await getAuthHeaders();
  const res = await fetch(`${API_URL}${path}`, { headers });
  if (!res.ok) {
    const body = await parseErrorBody(res);
    throw new ApiError(res.status, res.statusText, body);
  }
  return res.json() as Promise<T>;
}

/**
 * POST JSON to the Synapse API with automatic auth headers.
 * WHY: Mutation endpoints (manual entry, governance actions) need POST support.
 * Throws on non-2xx responses so TanStack Query mutation hooks surface errors.
 */
export async function apiPost<T>(path: string, body: unknown): Promise<T> {
  const authHeaders = await getAuthHeaders();
  const res = await fetch(`${API_URL}${path}`, {
    method: 'POST',
    headers: {
      ...authHeaders,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const errBody = await parseErrorBody(res);
    throw new ApiError(res.status, res.statusText, errBody);
  }
  return res.json() as Promise<T>;
}

/**
 * PATCH JSON to the Synapse API with automatic auth headers.
 * WHY: Governance update endpoints use PATCH for partial updates.
 */
export async function apiPatch<T>(path: string, body: unknown): Promise<T> {
  const authHeaders = await getAuthHeaders();
  const res = await fetch(`${API_URL}${path}`, {
    method: 'PATCH',
    headers: {
      ...authHeaders,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const errBody = await parseErrorBody(res);
    throw new ApiError(res.status, res.statusText, errBody);
  }
  return res.json() as Promise<T>;
}

/**
 * PUT JSON to the Synapse API with automatic auth headers.
 * WHY: Owner statement upsert uses PUT.
 */
export async function apiPut<T>(path: string, body: unknown): Promise<T> {
  const authHeaders = await getAuthHeaders();
  const res = await fetch(`${API_URL}${path}`, {
    method: 'PUT',
    headers: {
      ...authHeaders,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const errBody = await parseErrorBody(res);
    throw new ApiError(res.status, res.statusText, errBody);
  }
  return res.json() as Promise<T>;
}

/**
 * DELETE to the Synapse API with automatic auth headers.
 * WHY: Governance discard/undesignate endpoints use DELETE.
 * Some DELETE endpoints accept a JSON body (e.g., top-three undesignation).
 */
export async function apiDelete<T = void>(path: string, body?: unknown): Promise<T> {
  const authHeaders = await getAuthHeaders();
  const options: RequestInit = {
    method: 'DELETE',
    headers: { ...authHeaders },
  };
  if (body !== undefined) {
    (options.headers as Record<string, string>)['Content-Type'] = 'application/json';
    options.body = JSON.stringify(body);
  }
  const res = await fetch(`${API_URL}${path}`, options);
  if (!res.ok) {
    const errBody = await parseErrorBody(res);
    throw new ApiError(res.status, res.statusText, errBody);
  }
  // WHY: 204 No Content responses have no body to parse.
  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
}

/**
 * POST multipart FormData to the Synapse API with automatic auth headers.
 * WHY: SecurityStudio document upload requires multipart/form-data. The browser
 * sets Content-Type with the correct boundary automatically when body is FormData.
 * Requirements: 18.1
 */
export async function apiUpload<T>(path: string, formData: FormData): Promise<T> {
  const authHeaders = await getAuthHeaders();
  const res = await fetch(`${API_URL}${path}`, {
    method: 'POST',
    headers: { ...authHeaders },
    body: formData,
  });
  if (!res.ok) {
    const errBody = await parseErrorBody(res);
    throw new ApiError(res.status, res.statusText, errBody);
  }
  return res.json() as Promise<T>;
}
